import React, { FC, useState, useEffect } from 'react';
import { useTranslation } from 'react-i18next';
import { DrawerPanel } from '../ui/DrawerPanel';
import { getConfigurationBySenior, updateManagedMode } from '../../lib/supabase';
import { API_BASE_URL } from '../../config/apiUrl';
import { syncSeniorConfig } from '../../lib/syncConfig';

interface ManagedModeDrawerProps {
  isOpen: boolean;
  onClose: () => void;
  seniorId: string;
  seniorName: string;
}

/**
 * Managed Mode panel (PR #2).
 * Managed Mode locks the phone into the AidFone launcher; leaving it requires
 * a 4-digit caregiver PIN. This drawer is the ONLY caregiver-facing surface for
 * enabling/disabling Managed Mode and setting/changing that PIN — before this,
 * the PIN could only be set by hand in the DB.
 *
 * Save writes `managed_mode_pin` / `managed_mode_enabled` to the configuration,
 * then POSTs to /seniors/:id/sync-config so the server pushes the change to the
 * device via Firebase (the device hashes the PIN on receipt — plain text never
 * lands on the phone).
 */
export const ManagedModeDrawer: FC<ManagedModeDrawerProps> = ({
  isOpen,
  onClose,
  seniorId,
  seniorName,
}) => {
  const { t } = useTranslation();

  const [loading, setLoading] = useState(false);
  const [enabled, setEnabled] = useState(true);
  const [pinIsSet, setPinIsSet] = useState(false);
  const [pin, setPin] = useState('');
  const [confirmPin, setConfirmPin] = useState('');
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState<string | null>(null);
  // Separate from `error`: a FAILED LOAD must block saving (we'd be writing on
  // top of default state), whereas a failed SAVE must still allow a retry.
  const [loadFailed, setLoadFailed] = useState(false);

  // Load current managed-mode state when the drawer opens.
  useEffect(() => {
    if (!isOpen || !seniorId) return;
    let cancelled = false;
    setLoading(true);
    setError(null);
    setLoadFailed(false);
    setPin('');
    setConfirmPin('');
    (async () => {
      try {
        const config = await getConfigurationBySenior(seniorId);
        if (cancelled) return;
        setEnabled(config?.managed_mode_enabled ?? true);
        setPinIsSet(Boolean(config?.managed_mode_pin));
      } catch (err) {
        if (cancelled) return;
        console.error('[ManagedMode] Failed to load config:', err);
        setError(t('drawers.managedMode.loadError'));
        setLoadFailed(true);
      } finally {
        if (!cancelled) setLoading(false);
      }
    })();
    return () => {
      cancelled = true;
    };
    // `t` is deliberately NOT a dependency: react-i18next hands back a new `t`
    // identity on every language change, which would re-run this load and wipe
    // half-typed PIN fields when the caregiver flips EN/FR.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isOpen, seniorId]);

  const pinDigits = (v: string) => v.replace(/\D/g, '').slice(0, 4);
  const wantsPinChange = pin.length > 0 || confirmPin.length > 0;
  const pinValid = pin.length === 4 && pin === confirmPin;
  // A PIN is required to ENABLE Managed Mode (it is the only way back out of the
  // locked launcher), but turning it OFF must always be possible — otherwise a
  // caregiver who never set a PIN is trapped, since Managed Mode defaults to on.
  const needsPinToEnable = enabled && !pinIsSet && !wantsPinChange;
  const canSave =
    !saving &&
    !loading &&
    // Never save on top of a failed load. `enabled` defaults to true, so if the
    // fetch failed for a senior who had Managed Mode OFF, the toggle would show
    // ON and saving would remotely re-lock their phone without the caregiver
    // ever asking for it. A failed SAVE does not block a retry.
    !loadFailed &&
    (wantsPinChange ? pinValid : !needsPinToEnable);

  const handleSave = async () => {
    setSaving(true);
    setError(null);
    try {
      const patch: { managed_mode_enabled: boolean; managed_mode_pin?: string } = {
        managed_mode_enabled: enabled,
      };
      if (wantsPinChange) patch.managed_mode_pin = pin;

      await updateManagedMode(seniorId, patch);

      // Push to the device via the server (device hashes the PIN on receipt).
      // Auth + ownership now required server-side; syncSeniorConfig attaches
      // the JWT and never throws (see lib/syncConfig.ts).
      await syncSeniorConfig(seniorId);

      onClose();
    } catch (err) {
      console.error('[ManagedMode] Save failed:', err);
      setError(t('drawers.managedMode.saveError'));
    } finally {
      setSaving(false);
    }
  };

  return (
    <DrawerPanel
      isOpen={isOpen}
      onClose={onClose}
      title={t('drawers.managedMode.title')}
      footer={
        <button
          onClick={handleSave}
          disabled={!canSave}
          className="w-full py-3 bg-brand-primary text-white font-semibold rounded-xl disabled:opacity-50 disabled:cursor-not-allowed flex items-center justify-center gap-2"
        >
          {saving ? (
            <>
              <span className="material-symbols-outlined text-lg animate-spin">progress_activity</span>
              {t('drawers.managedMode.saving')}
            </>
          ) : (
            t('drawers.managedMode.save')
          )}
        </button>
      }
    >
      {loading ? (
        <div className="flex flex-col gap-3 animate-pulse">
          <div className="h-16 bg-brand-bg-warm rounded-xl" />
          <div className="h-12 bg-brand-bg-warm rounded-xl" />
          <div className="h-12 bg-brand-bg-warm rounded-xl" />
        </div>
      ) : (
        <div className="flex flex-col gap-5">
          <p className="text-sm text-brand-text-muted leading-relaxed">
            {t('drawers.managedMode.description', { name: seniorName })}
          </p>

          {/* Enabled toggle */}
          <label className="flex items-center justify-between gap-3 p-4 rounded-xl border border-brand-border">
            <span className="flex flex-col">
              <span className="text-sm font-semibold text-brand-text">
                {t('drawers.managedMode.enabledLabel')}
              </span>
              <span className="text-xs text-brand-text-muted">
                {t('drawers.managedMode.enabledHint')}
              </span>
            </span>
            <button
              type="button"
              role="switch"
              aria-checked={enabled}
              onClick={() => setEnabled((v) => !v)}
              className={`relative w-11 h-6 rounded-full flex-shrink-0 transition-colors ${
                enabled ? 'bg-brand-primary' : 'bg-brand-border'
              }`}
            >
              <span
                className={`absolute top-0.5 left-0.5 w-5 h-5 rounded-full bg-white shadow transition-transform ${
                  enabled ? 'translate-x-5' : 'translate-x-0'
                }`}
              />
            </button>
          </label>

          {/* PIN status + set/change */}
          <div className="flex flex-col gap-3">
            <div className="flex items-center gap-2 text-sm">
              <span
                className={`material-symbols-outlined ${pinIsSet ? 'text-brand-success' : 'text-amber-600'}`}
                style={{ fontSize: '18px' }}
              >
                {pinIsSet ? 'lock' : 'lock_open'}
              </span>
              <span className={pinIsSet ? 'text-brand-text' : 'text-amber-700 font-medium'}>
                {pinIsSet
                  ? t('drawers.managedMode.pinSet')
                  : t('drawers.managedMode.pinNotSet')}
              </span>
            </div>

            <label className="flex flex-col gap-2">
              <span className="text-sm font-medium text-brand-text">
                {pinIsSet
                  ? t('drawers.managedMode.newPinLabel')
                  : t('drawers.managedMode.pinLabel')}
              </span>
              <input
                type="password"
                inputMode="numeric"
                autoComplete="new-password"
                value={pin}
                onChange={(e) => setPin(pinDigits(e.target.value))}
                placeholder="••••"
                maxLength={4}
                className="px-4 py-3 rounded-xl border border-brand-border bg-white text-brand-text tracking-[0.5em] text-center text-lg focus:border-brand-primary focus:ring-2 focus:ring-brand-primary/20 outline-none"
              />
            </label>

            <label className="flex flex-col gap-2">
              <span className="text-sm font-medium text-brand-text">
                {t('drawers.managedMode.confirmPinLabel')}
              </span>
              <input
                type="password"
                inputMode="numeric"
                autoComplete="new-password"
                value={confirmPin}
                onChange={(e) => setConfirmPin(pinDigits(e.target.value))}
                placeholder="••••"
                maxLength={4}
                className="px-4 py-3 rounded-xl border border-brand-border bg-white text-brand-text tracking-[0.5em] text-center text-lg focus:border-brand-primary focus:ring-2 focus:ring-brand-primary/20 outline-none"
              />
            </label>

            {wantsPinChange && !pinValid && (
              <p className="text-xs text-amber-700">
                {pin.length !== 4
                  ? t('drawers.managedMode.pinLengthHint')
                  : t('drawers.managedMode.pinMismatch')}
              </p>
            )}
            {needsPinToEnable && (
              <p className="text-xs text-amber-700">
                {t('drawers.managedMode.pinRequiredToEnable')}
              </p>
            )}
          </div>

          {error && (
            <div className="p-3 rounded-lg bg-red-50 border border-red-200 text-sm text-red-700 flex items-center gap-2">
              <span className="material-symbols-outlined text-lg">error</span>
              {error}
            </div>
          )}

          <p className="text-xs text-brand-text-muted">
            {t('drawers.managedMode.deployNote', { name: seniorName })}
          </p>
        </div>
      )}
    </DrawerPanel>
  );
};

export default ManagedModeDrawer;
